import { useEffect, useState } from 'react';
import type { ApiClient } from '../../api';
import type {
  MsrAnlageDetail, MsrAnlageZeile, MsrAspDetail, MsrBaum as MsrBaumTyp,
  MsrFeldgeraetDetail,
} from './typen';
import { GEWERK } from './gemeinsam';
import { Knoten } from './Knoten';
import { AnlageDetail } from './AnlageDetail';
import { AggregatDetail } from './AggregatDetail';
import { RANG_SEITE, msrZiele, useMsrExport, zeitpunkt } from './kopf';
import {
  ContentSection, DataTable, EmptyState, ErrorState, Feld, LoadingState,
  PropertyGrid, StatusBadge, type Spalte,
} from '../../muster';

/**
 * Reiter „Baum" — links der Baum (nach Gewerk oder nach ASP, Suche im
 * Browser), rechts der gewählte Knoten. Lädt selbst nach: `baum` einmal beim
 * Umschalten, das Detail je Auswahl (Gewerk → Anlagenliste, ASP → `asps/{id}`,
 * Anlage → `anlagen/{id}`, Aggregat → `feldgeraete/{id}`).
 */
export function Baum({
  api, projektId, nach, onNachChange,
}: {
  api: ApiClient;
  projektId: string;
  nach: 'gewerk' | 'asp';
  onNachChange: (n: 'gewerk' | 'asp') => void;
}) {
  const [baum, setBaum] = useState<MsrBaumTyp | null>(null);
  const [baumLaedt, setBaumLaedt] = useState(false);
  const [baumFehler, setBaumFehler] = useState<string | null>(null);
  // TM-8c: „Erneut versuchen“ lädt den Baum neu - vorher rief es onNachChange mit demselben Wert (ohne Wirkung).
  const [baumVersuch, setBaumVersuch] = useState(0);
  const [suche, setSuche] = useState('');
  const [offen, setOffen] = useState<Set<string>>(new Set());

  const [gewaehlt, setGewaehlt] = useState<
    { id: string; art: string } | null>(null);

  const [gewerkAnlagen, setGewerkAnlagen] = useState<MsrAnlageZeile[] | null>(null);
  const [asp, setAsp] = useState<MsrAspDetail | null>(null);
  const [anlage, setAnlage] = useState<MsrAnlageDetail | null>(null);
  const [feldgeraet, setFeldgeraet] = useState<MsrFeldgeraetDetail | null>(null);
  const [detailFehler, setDetailFehler] = useState<string | null>(null);
  useMsrExport(RANG_SEITE, () => [msrZiele.feldgeraete(api, projektId)], [api, projektId]);

  useEffect(() => {
    setBaumLaedt(true);
    setBaumFehler(null);
    api.msrBaum(projektId, nach)
      .then((b) => { setBaum(b); setBaumLaedt(false); })
      .catch((e) => {
        setBaumFehler(e instanceof Error ? e.message : String(e));
        setBaumLaedt(false);
      });
  }, [api, projektId, nach, baumVersuch]);

  const anlageNeuLaden = () => {
    if (gewaehlt?.art === 'anlage') {
      api.msrAnlage(projektId, gewaehlt.id).then(setAnlage);
    }
  };

  useEffect(() => {
    if (!gewaehlt) return;

    setGewerkAnlagen(null); setAsp(null); setAnlage(null); setFeldgeraet(null);
    setDetailFehler(null);

    // Die id eines Gewerk-Knotens im Baum ist 'g:<schluessel>' (siehe
    // MsrBaumKnoten.id im Vertrag) — der Schlüssel selbst steht hinter dem
    // ersten Doppelpunkt.
    const roh = gewaehlt.id.includes(':')
      ? gewaehlt.id.slice(gewaehlt.id.indexOf(':') + 1) : gewaehlt.id;

    const laden = async () => {
      try {
        if (gewaehlt.art === 'gewerk') {
          setGewerkAnlagen(await api.msrAnlagen(projektId, { gewerk: roh }));
        } else if (gewaehlt.art === 'asp') {
          setAsp(await api.msrAsp(projektId, roh));
        } else if (gewaehlt.art === 'anlage') {
          setAnlage(await api.msrAnlage(projektId, roh));
        } else if (gewaehlt.art === 'aggregat') {
          setFeldgeraet(await api.msrFeldgeraet(projektId, roh));
        }
      } catch (e) {
        setDetailFehler(e instanceof Error ? e.message : String(e));
      }
    };

    laden();
  }, [api, projektId, gewaehlt]);

  const gewerkSpalten: Spalte<MsrAnlageZeile>[] = [
    { schluessel: 'kennung', titel: 'Kennung', wert: (a) => a.kennung, immerSichtbar: true },
    { schluessel: 'anlagenart', titel: 'Anlagenart', wert: (a) => a.anlagenart },
    { schluessel: 'geschoss', titel: 'Geschoss', wert: (a) => a.geschoss ?? null },
    { schluessel: 'aspKennung', titel: 'ASP', wert: (a) => a.aspKennung ?? null },
    { schluessel: 'feldgeraete', titel: 'Feldgeräte', art: 'zahl',
      wert: (a) => a.feldgeraete },
    { schluessel: 'datenpunkte', titel: 'Datenpunkte', art: 'zahl',
      wert: (a) => a.datenpunkte?.summe ?? null },
  ];

  const toggle = (id: string) => setOffen((o) => {
    const neu = new Set(o);
    if (neu.has(id)) neu.delete(id); else neu.add(id);
    return neu;
  });

  return (
    <div style={{ display: 'flex', gap: 14, alignItems: 'flex-start', flexWrap: 'wrap' }}>
      <div className="baum" style={{ minWidth: 300, maxWidth: 360 }}>
        <div style={{ padding: 8, borderBottom: '1px solid var(--linie)' }}>
          <div style={{ display: 'flex', gap: 4, marginBottom: 8 }}>
            <button type="button"
              className={`reiter-knopf${nach === 'gewerk' ? ' aktiv' : ''}`}
              style={{ flex: 1, fontSize: 12 }}
              onClick={() => { onNachChange('gewerk'); setGewaehlt(null); }}>
              nach Gewerk
            </button>
            <button type="button"
              className={`reiter-knopf${nach === 'asp' ? ' aktiv' : ''}`}
              style={{ flex: 1, fontSize: 12 }}
              onClick={() => { onNachChange('asp'); setGewaehlt(null); }}>
              nach ASP
            </button>
          </div>

          <Feld art="suche" beschriftung="Knoten suchen" wert={suche}
            platzhalter="Bezeichnung …"
            beiAenderung={(w) => setSuche(w as string)} />
        </div>

        {baumLaedt && <LoadingState text="Baum wird abgerufen …" zeilen={8} />}
        {baumFehler && (
          <ErrorState
            klein
            meldung="Der MSR-Baum konnte nicht geladen werden."
            technisch={baumFehler}
            beiErneutVersuchen={() => setBaumVersuch((v) => v + 1)}
          />
        )}

        {!baumLaedt && (baum?.wurzeln ?? []).map((k) => (
          <Knoten key={k.id} knoten={k} ebene={0}
            gewaehlt={gewaehlt?.id ?? null}
            onWaehlen={(id, art) => setGewaehlt({ id, art })}
            offen={offen} onToggle={toggle} suche={suche} />
        ))}

        {!baumLaedt && !baumFehler && (baum?.wurzeln ?? []).length === 0 && (
          <EmptyState
            klein
            titel="Der Baum ist leer."
            erklaerung={'Er entsteht aus dem Ableitungslauf — solange keiner gelaufen ist, '
              + 'gibt es weder Gewerke noch Schaltschränke.'}
          />
        )}

        {baum?.stand && (
          <div className="schwach" style={{ padding: 8, fontSize: 11 }}>
            Stand: {zeitpunkt(baum.stand)}
          </div>
        )}
      </div>

      <div style={{ flex: '1 1 640px', minWidth: 320 }}>
        {!gewaehlt && (
          <EmptyState
            titel="Kein Knoten gewählt."
            erklaerung={'Links einen Knoten wählen — rechts erscheint das Gewerk, der '
              + 'Schaltschrank, die Anlage oder das Aggregat mit seinen Datenpunkten.'}
          />
        )}

        {detailFehler && (
          <ErrorState
            meldung="Der gewählte Knoten konnte nicht geladen werden."
            technisch={detailFehler}
            beiErneutVersuchen={() => setGewaehlt((g) => (g ? { ...g } : g))}
          />
        )}

        {gewaehlt?.art === 'gewerk' && (
          <ContentSection
            titel={GEWERK[gewaehlt.id.slice(gewaehlt.id.indexOf(':') + 1) as keyof typeof GEWERK]
              ?? gewaehlt.id}
            beschreibung="Alle Anlagen dieses Gewerks — eine Zeile öffnet die Anlage."
          >
            <DataTable
              name="Anlagen"
              spalten={gewerkSpalten}
              zeilen={gewerkAnlagen ?? []}
              schluessel={(a) => a.id}
              laedt={gewerkAnlagen === null && detailFehler === null}
              dicht
              ausfuhr
              ausfuhrDateiname="msr-anlagen-je-gewerk"
              beiZeileKlick={(a) => setGewaehlt({ id: a.id, art: 'anlage' })}
              leer={(
                <EmptyState
                  titel="Für dieses Gewerk ist keine Anlage abgeleitet."
                  erklaerung={'Anlagen entstehen aus dem Gewerkemodul — ohne aufgenommene '
                    + 'Anlage bleibt der Zweig leer.'}
                />
              )}
            />
          </ContentSection>
        )}

        {gewaehlt?.art === 'asp' && (
          asp
            ? <AspKurzdetail asp={asp} />
            : <LoadingState text="Schaltschrank wird abgerufen …" zeilen={4} spalten={3} />
        )}

        {gewaehlt?.art === 'anlage' && (
          <AnlageDetail api={api} projektId={projektId} anlage={anlage}
            onNeuLaden={anlageNeuLaden} />
        )}

        {gewaehlt?.art === 'aggregat' && (
          <AggregatDetail feldgeraet={feldgeraet} />
        )}
      </div>
    </div>
  );
}

/**
 * Kurzform des ASP-Details im Baum — die vollständige Ansicht mit Kapazität
 * pflegen und ASP-Blatt steht im Reiter „Schaltschränke / ASPs".
 */
function AspKurzdetail({ asp }: { asp: MsrAspDetail }) {
  return (
    <ContentSection
      titel={asp.kennung}
      beschreibung={'Kurzform — Kapazität pflegen und ASP-Blatt stehen im Reiter '
        + '„Schaltschränke / ASPs".'}
      aktionen={asp.ueberKapazitaet
        ? <StatusBadge zustand="fehler" text="über Kapazität" />
        : <StatusBadge zustand="gut" text="im Rahmen" />}
    >
      <PropertyGrid
        spalten={3}
        eintraege={[
          { beschriftung: 'Art', wert: asp.art },
          { beschriftung: 'Geschoss', wert: asp.geschoss ?? null },
          { beschriftung: 'Raum', wert: asp.raumname ?? asp.raumnummer ?? null },
          { beschriftung: 'Felder', wert: asp.felder },
          { beschriftung: 'Einspeisung', wert: asp.einspeisung ?? null },
          { beschriftung: 'Auslastung', wert: asp.auslastungProzent, einheit: '%' },
        ]}
      />
    </ContentSection>
  );
}
